import { useDeleteUser, useFetchUserList } from '@api';
import {
  AdminModalType,
  PathnameEnum,
  PathParamKeyEnum,
  QueryParamKeyEnum,
  ToastMessageEnum,
  UserRoleEnum
} from '@constant';
import { useSelector } from '@stores';
import { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useToast } from './useToast';

export const useAdminUserList = () => {
  const [modalType, setModalType] = useState<AdminModalType>(AdminModalType.USER_DELETE_CONFIRM);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const { showToast } = useToast();

  const userRole = useSelector((state) => state.auth.role);

  const tableHeader = [
    { id: 'name', label: '이름', width: 332 },
    { id: 'email', label: '이메일', width: 332 },
    { id: 'contact', label: '연락처', width: 332 },
    { id: 'IP', label: '허용 IP', width: 332 },
    { id: 'permission', label: '권한', width: 200 },
    { id: 'deleteButton', label: '', width: 72 }
  ];

  const navigate = useNavigate();
  const location = useLocation();
  const locationPathname = location.pathname;

  const [searchParams] = useSearchParams();
  const page = searchParams.get(QueryParamKeyEnum.PAGE) ? Number(searchParams.get(QueryParamKeyEnum.PAGE)) : 1;

  const { response, refetch } = useFetchUserList(page);

  const { mutateAsync: deleteUserMutateAsync, isPending: isDeleteUserPending } = useDeleteUser();

  const handleCreateButtonClick = () => {
    navigate(PathnameEnum.ADMIN_USER_CREATE_PAGE);
  };

  const handleTableRowClick = (userId: string) => {
    navigate(`${PathnameEnum.ADMIN_USER_EDIT_PAGE.replace(PathParamKeyEnum.USER_ID, String(userId))}`);
  };

  const handlePageChange = (value: number) => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set(QueryParamKeyEnum.PAGE, String(value));
    const queryString = queryParams.toString();
    navigate(`${locationPathname}?${queryString}`, { replace: true });
  };

  // 회원 삭제 또는 회원 수정 페이지 이동 권한 체크 함수
  const checkPermission = (event: 'rowClick' | 'delete', rowRole: UserRoleEnum) => {
    if (userRole === UserRoleEnum['루트 관리자']) {
      if (event === 'rowClick') {
        return true;
      } else if (event === 'delete') {
        return rowRole !== UserRoleEnum['루트 관리자'];
      }
    }
    if (userRole === UserRoleEnum['최고 관리자']) {
      return rowRole !== UserRoleEnum['루트 관리자'] && rowRole !== UserRoleEnum['최고 관리자'];
    }
    return false;
  };

  const handleDeleteButtonClick = (userId: string) => {
    setDeleteTarget(userId);
    setModalType(AdminModalType.USER_DELETE_CONFIRM);
    setIsModalVisible(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteTarget(null);
    setIsModalVisible(false);
  };

  const handleDeleteModalConfirmClick = async () => {
    if (deleteTarget) {
      await deleteUserMutateAsync(deleteTarget)
        .then(() => {
          setDeleteTarget(null);
          refetch();
          setIsModalVisible(false);
          handleDeleteModalClose();
          showToast(ToastMessageEnum.USER_DELETE_SUCCESS);
        })
        .catch(() => {
          setModalType(AdminModalType.USER_DELETE_FAIL);
        });
    }
  };

  return {
    handleCreateButtonClick,
    handleTableRowClick,

    tableHeader,
    response,
    page,
    handlePageChange,

    checkPermission,

    modalType,
    isModalVisible,
    handleDeleteButtonClick,
    handleDeleteModalClose,
    handleDeleteModalConfirmClick,

    isDeleteUserPending
  };
};
